<template>
    <div>
        <div class="row">
            <div class="col-md-auto pr-md-0">
                <h5 class="title">Score logbook</h5>
            </div>
            <div class="col ml-md-0">
                <icons icon="help" tooltip="Keep track of changes within the score by adding score objects."></icons>
            </div>
            <div class="col">
                <button type="button" aria-label="Close" class="close" @click="$bvModal.hide(modalId)">×</button>
            </div>
        </div>
        <div class="row mt-md-2">
            <div class="col">
                <button class="btn-custom btn btn-secondary button-add" @click="addNewItem">Add new score</button>
            </div>
        </div>
        <div class="row mt-md-4">
            <div class="col-md-2">
                <label class="card">Date</label>
            </div>
            <div class="col-md-3">
                <div class="row">
                    <div class="col-md-auto pr-md-0"><label class="card">Score</label></div>
                    <div class="col-md-auto" @click="showHelptextScore">
                        <icons icon="help" tooltip="Click to open more information."></icons>
                    </div>
                </div>
            </div>
            <div class="col-md-5">
                <label class="card">Comment</label>
            </div>
            <div v-if="showAutoGenerated" class="col-md-auto">
                <div class="row">
                    <div class="col pr-md-0"><label class="card">Auto generated</label></div>
                    <div class="col-md-auto pl-md-1">
                        <icons
                            icon="help"
                            tooltip="Indicates if the score was auto-generated based on the number of data sources within the data source administration file (using the option -y, --yaml from the datasource mode in dettect.py) . When manually scoring your visibility you can set it to False. The purpose of having this property is to allow an easy update of your visibility scores based on changes in your data sources. Or modification within MITRE\'s semi-annual update of ATT&CK (possible new techniques, data sources or changes in the data source listed for a particular technique). For more info see the wiki pages on Github."
                        ></icons>
                    </div>
                </div>
            </div>
            <div class="col-md-0"></div>
        </div>
        <div v-for="(v, index) in item" :key="index">
            <div class="row score-logbook">
                <div class="col-md-2 pr-md-0">
                    <date-picker
                        :showLabel="false"
                        :date="v.date"
                        name="Date"
                        @dateUpdated="updateDate(index, $event)"
                        :id="index.toString()"
                    ></date-picker>
                </div>
                <div class="col-md-3">
                    <score-slider
                        name="score"
                        :score="v.score"
                        :markData="scores"
                        :markDataTooltip="scoresTooltip"
                        @scoreUpdated="
                            v.score = $event;
                            if (showAutoGenerated) {
                                v.auto_generated = false;
                            }
                        "
                        :showLabel="false"
                    ></score-slider>
                </div>
                <div class="col-md-5">
                    <extended-textarea
                        :data_object="v"
                        data_field="comment"
                        :id="'modal' + index"
                        :cb_function="cb_function"
                        rows="4"
                    ></extended-textarea>
                </div>
                <div v-if="showAutoGenerated" class="col-md-auto">
                    <toggle-button
                        :state="v.auto_generated"
                        name="auto_generated"
                        @toggleButtonUpdated="v.auto_generated = $event"
                        :showLabel="false"
                    ></toggle-button>
                </div>
                <div class="col-md-0">
                    <i class="tim-icons icon-trash-simple icon-color icon-padding cursor-pointer" @click="deleteProperty(index)"></i>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from '@/components/Inputs/DatePicker';
import ScoreSlider from '@/components/Inputs/ScoreSlider';
import ToggleButton from '@/components/Inputs/ToggleButton';
import Icons from '@/components/Icons';
import ExtendedTextarea from '@/components/Inputs/ExtendedTextarea';
import _ from 'lodash';
import { notificationMixin } from '@/mixins/NotificationMixins.js';

export default {
    data() {
        return {
            newScore: this.defaultScore
        };
    },
    mixins: [notificationMixin],
    props: {
        item: {
            type: Array,
            required: true
        },
        scores: {
            type: Array,
            required: true
        },
        scoresTooltip: {
            type: Object,
            required: true
        },
        defaultScore: {
            type: Number,
            required: true
        },
        showAutoGenerated: {
            type: Boolean,
            required: false,
            default: false
        },
        modalId: {
            type: String,
            required: true
        },
        emptyScoreEntry: {
            type: Object,
            required: true
        },
        cb_function: {
            type: Function,
            required: false
        }
    },
    components: {
        DatePicker,
        ScoreSlider,
        ToggleButton,
        Icons,
        ExtendedTextarea
    },
    mounted() {
        this.sortOnDates();
    },
    methods: {
        addNewItem() {
            for (let i = 0; i < this.item.length; i++) {
                if (this.item[i].date == null) {
                    return;
                }
            }
            let newItem = _.cloneDeep(this.emptyScoreEntry);
            newItem.score = this.defaultScore;
            this.item.push(newItem);

            this.sortOnDates();
        },
        updateDate(index, event) {
            if (event != '' && this.isNotDuplicate(index, event)) {
                this.item[index].date = event;
            }
        },
        deleteProperty(index) {
            this.item.splice(index, 1);
        },
        isNotDuplicate(index, d) {
            for (let i = 0; i < this.item.length; i++) {
                if (index != i && this.item[i].date == d) {
                    let title = 'Duplicate date';
                    let msg = "The date '" + d + "' already exists in the score logbook. Duplicate entries are not allowed.";
                    this.notifyWarning(title, msg);
                    return false;
                }
            }
            return true;
        },
        sortOnDates() {
            let sorted = _.sortBy(this.item, 'date');

            // Empty the array and fill it with the sorted content. Empty the array with a while loop, because this.item = []
            // gives a warning on the JavaScript console:
            //    Avoid mutating a prop directly since the value will be overwritten whenever the parent component re-renders.
            //    Instead, use a data or computed property based on the prop's value. Prop being mutated: "item"
            while (this.item.length > 0) {
                this.item.pop();
            }

            for (let i = 0; i < sorted.length; i++) {
                this.item.push(sorted[i]);
            }
            this.item.reverse();
        },
        showHelptextScore(event) {
            this.$emit('showHelptextScoreNow', event);
        }
    }
};
</script>

<style></style>
